import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Bell, Mail, Trash2, Zap, Calendar, Plus, AlertCircle, Send, ArrowRight, Flame } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { BrutalButton, BentoCard } from "@/components/dashboard/BentoCard";
import { PushSettings } from "@/components/alerts/PushSettings";
import { HexBadge } from "@/components/dashboard/Hex";
import { ALERTS } from "@/constants/testIds";

const CADENCE_LABEL = {
  realtime: { label: "Real-time", icon: Zap, tone: "running" },
  daily: { label: "Daily digest", icon: Calendar, tone: "default" },
  both: { label: "Real-time + digest", icon: Bell, tone: "success" },
};

export default function AlertsPage() {
  const { user, isAuthenticated } = useAuth();
  const queryClient = useQueryClient();

  const { data: searches = [], isLoading } = useQuery({
    queryKey: ["alerts", "searches"],
    queryFn: async () => (await api.get("/alerts/searches")).data,
    enabled: isAuthenticated,
  });

  const { data: status } = useQuery({
    queryKey: ["alerts", "status"],
    queryFn: async () => (await api.get("/alerts/status")).data,
    enabled: isAuthenticated,
  });

  const deleteMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/alerts/searches/${id}`)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["alerts", "searches"] });
      toast.success("Alert removed");
    },
    onError: () => toast.error("Could not remove alert"),
  });

  const testMut = useMutation({
    mutationFn: async () => (await api.post("/alerts/test", {})).data,
    onSuccess: (r) => {
      if (r.status === "sent") toast.success(`Sample sent to ${user?.email}`);
      else toast(`Queued (${r.reason || "pending"}). Add your SendGrid key to deliver.`, { icon: "📮" });
    },
    onError: () => toast.error("Test send failed"),
  });

  const topDealsMut = useMutation({
    mutationFn: async () => (await api.post("/alerts/top-deals-digest/test", {})).data,
    onSuccess: (r) => {
      if (r.status === "no_deals") toast("No scored listings yet — run a scrape first.", { icon: "📭" });
      else if (r.status === "sent") toast.success(`Top ${r.deal_count} digest sent to ${user?.email}`);
      else toast(`Queued (${r.deal_count} deals). It'll deliver once email is configured.`, { icon: "📮" });
    },
    onError: () => toast.error("Could not send weekly preview"),
  });

  if (!isAuthenticated) {
    return (
      <div className="max-w-lg">
        <div className="data-label">Alerts</div>
        <h1 className="font-display text-4xl font-bold tracking-tight">Sign in to set alerts</h1>
        <p className="text-sm text-zinc-600 mt-2">
          Save a search and Lien Scout will email you the moment new matching listings hit the feed.
        </p>
        <Link
          to="/login"
          className="mt-6 inline-flex items-center gap-2 px-4 py-2 border-2 border-black bg-yellow-400 font-display font-semibold shadow-brutal-sm"
        >
          Sign in with Google <ArrowRight size={14} />
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid={ALERTS.root}>
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="data-label">Automation</div>
          <h1 className="font-display text-4xl font-bold tracking-tight">Alerts</h1>
          <p className="text-sm text-zinc-600 mt-2">
            The shortlist finds <em>you</em>. Save any Properties-page filter combo and get an email the moment
            a match lands.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <BrutalButton
            variant="secondary"
            data-testid={ALERTS.testBtn}
            onClick={() => testMut.mutate()}
            disabled={testMut.isPending}
          >
            <Send size={14} /> Send test email
          </BrutalButton>
          <BrutalButton
            variant="secondary"
            data-testid="alerts-weekly-preview-btn"
            onClick={() => topDealsMut.mutate()}
            disabled={topDealsMut.isPending}
          >
            <Flame size={14} /> {topDealsMut.isPending ? "Sending…" : "Preview weekly Top 10"}
          </BrutalButton>
          <Link
            to="/properties"
            className="inline-flex items-center gap-2 px-4 py-2 border-2 border-black bg-red-600 text-white font-display font-semibold text-sm shadow-brutal-sm hover:bg-red-500"
          >
            <Plus size={16} strokeWidth={2.5} /> New alert from Properties
          </Link>
        </div>
      </div>

      {status && !status.sendgrid_configured ? (
        <div
          data-testid={ALERTS.statusBanner}
          className="flex items-start gap-3 border-2 border-yellow-500 bg-yellow-50 px-4 py-3 shadow-brutal-sm"
        >
          <AlertCircle className="text-yellow-700 mt-0.5" size={18} />
          <div className="flex-1 text-sm">
            <div className="font-display font-semibold">Email delivery is warming up.</div>
            <p className="text-zinc-700 mt-0.5">
              Your alerts are being <strong>queued</strong> until SendGrid finishes verifying{" "}
              <span className="font-mono-data">{status.from_email}</span>. Once your API key is added
              and DNS propagates, the queue auto-flushes every ~2 minutes — nothing to re-do here.
            </p>
          </div>
          <HexBadge tone="running">{status.pending_emails} queued</HexBadge>
        </div>
      ) : null}

      <PushSettings />

      {isLoading ? (
        <div className="text-sm text-zinc-500 font-mono-data uppercase tracking-widest">Loading…</div>
      ) : searches.length === 0 ? (
        <BentoCard data-testid={ALERTS.emptyState} className="text-center py-14">
          <Bell size={40} className="mx-auto text-yellow-400" strokeWidth={1.5} />
          <p className="mt-3 font-display font-bold text-xl">No alerts yet.</p>
          <p className="text-sm text-zinc-600 mt-2 max-w-md mx-auto">
            Head to <strong>Properties</strong>, dial in your filters (state, city, max amount, min deal
            score), then hit <strong>Save Search</strong>. Every future match lands in your inbox.
          </p>
          <Link
            to="/properties"
            className="mt-5 inline-flex items-center gap-2 px-4 py-2 border-2 border-black bg-red-600 text-white font-display font-semibold text-sm shadow-brutal-sm hover:bg-red-500"
          >
            Open Properties <ArrowRight size={14} strokeWidth={2.5} />
          </Link>
        </BentoCard>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {searches.map((s) => (
            <AlertCard key={s.id} search={s} onDelete={() => deleteMut.mutate(s.id)} />
          ))}
        </div>
      )}
    </div>
  );
}

function AlertCard({ search, onDelete }) {
  const meta = CADENCE_LABEL[search.cadence] || CADENCE_LABEL.both;
  const Icon = meta.icon;
  const filterChips = Object.entries(search.filters || {}).filter(([, v]) => v !== "" && v !== null && v !== undefined);

  return (
    <BentoCard data-testid={ALERTS.card(search.id)} className="!p-5">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="data-label flex items-center gap-2">
            <Icon size={12} strokeWidth={2.5} /> {meta.label}
          </div>
          <div className="font-display font-bold text-lg tracking-tight mt-1 truncate">
            {search.name}
          </div>
        </div>
        <button
          data-testid={ALERTS.deleteBtn(search.id)}
          onClick={onDelete}
          className="p-2 border-2 border-transparent hover:border-black hover:bg-red-50 text-zinc-500 hover:text-red-600"
          aria-label="Delete alert"
        >
          <Trash2 size={16} />
        </button>
      </div>

      <div className="mt-3 flex flex-wrap gap-1.5">
        {filterChips.length === 0 ? (
          <span className="font-mono-data text-[10px] uppercase tracking-widest text-zinc-400">
            No filters (all listings)
          </span>
        ) : (
          filterChips.map(([k, v]) => (
            <span
              key={k}
              className="inline-flex items-center gap-1 px-2 py-0.5 bg-yellow-50 border border-yellow-300 font-mono-data text-[11px]"
            >
              <span className="uppercase tracking-wider text-zinc-500">{k}</span>
              <span className="font-semibold text-zinc-800">{String(v)}</span>
            </span>
          ))
        )}
      </div>

      <div className="mt-4 grid grid-cols-2 gap-2 text-[11px] font-mono-data text-zinc-600">
        <div>
          <div className="uppercase tracking-widest text-zinc-400">Matches sent</div>
          <div className="font-semibold text-zinc-800 mt-0.5">{search.matches_seen ?? 0}</div>
        </div>
        <div>
          <div className="uppercase tracking-widest text-zinc-400">Last email</div>
          <div className="font-semibold text-zinc-800 mt-0.5">
            {search.last_emailed_at ? new Date(search.last_emailed_at).toLocaleDateString() : "—"}
          </div>
        </div>
      </div>
    </BentoCard>
  );
}
